// DrawerLayout.tsx
import { SwipeableDrawer, Box, Typography, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import CloseDrawerButton from '../CloseDrawerButton';
import tid from '@/common/infrastructure/config/tid';
import { BACKGROUND_COLORS, STATUS_COLORS } from '@/common/presentation/constants/colors';

type DrawerLayoutProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  legend?: ReactNode;
  footer?: ReactNode; // actions (rendered inside Content, sticky)
  width?: number | string; // default 560
  children: ReactNode; // form/content above the footer
  contentPadding?: string | number;
  keepMounted?: boolean;
  autoFooter?: boolean; // automatically add a footer with Accept button
  footerButtonText?: string;
  footerButtonSx?: React.CSSProperties;
  sx?: SxProps<Theme>;
  onAccept?: () => void;
  testId?: string;
};

export default function DrawerLayout({
  open,
  onClose,
  title,
  legend,
  contentPadding,
  width = 560,
  children,
  keepMounted,
  footer,
  autoFooter = false,
  footerButtonText = 'Accept',
  footerButtonSx,
  sx,
  onAccept,
  testId
}: DrawerLayoutProps) {
  const padding = contentPadding ?? 3;

  // Use explicit footer prop if provided, otherwise create auto footer if enabled
  const finalFooter =
    footer ||
    (autoFooter ? (
      <Box component="footer" sx={{ px: padding, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{
              height: '32px',
              width: '12.375rem',
              backgroundColor: STATUS_COLORS.INFO_DARK,
              color: BACKGROUND_COLORS.WHITE,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              ...footerButtonSx
            }}
            onClick={() => {
              // Allow apply handlers in children to process before unmounting the drawer.
              const result = onAccept?.();
              if (result && typeof (result as Promise<unknown>).then === 'function') {
                (result as Promise<unknown>).finally(() => {
                  requestAnimationFrame(() => onClose());
                });
              } else {
                requestAnimationFrame(() => onClose());
              }
            }}
            {...tid('common:drawer-layout:accept-button')}
          >
            {footerButtonText}
          </button>
        </Box>
      </Box>
    ) : undefined);
  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      sx={sx}
      ModalProps={{
        ...(keepMounted !== undefined && { keepMounted })
      }}
      SwipeAreaProps={{
        sx: {
          width: 0,
          pointerEvents: 'none'
        }
      }}
      PaperProps={{
        sx: {
          width,
          height: '100dvh',
          display: 'grid',
          gridTemplateRows: legend ? 'auto auto 1fr auto' : 'auto 1fr auto',
          overflow: 'hidden'
        },
        ...(testId ? tid(testId) : {})
      }}
    >
      {/* Title (fixed) */}
      <Box component="header" sx={{ display: 'flex', alignItems: 'center', gap: '14px', padding: padding }}>
        <CloseDrawerButton onClick={onClose} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {/* Legend (fixed, optional) */}
      {legend && <Box sx={{ padding: padding, py: 1.5 }}>{legend}</Box>}

      {/* Content: the ONLY scroll container */}
      <Box
        component="section"
        role="region"
        aria-label="Drawer content"
        sx={{
          overflow: 'auto',
          minHeight: 0, // allow the scroller to shrink
          display: 'flex',
          flexDirection: 'column',
          // Keep focusable elements visible above sticky footer
          scrollPaddingBottom: 24
        }}
      >
        {/* Main form/content */}
        <Box sx={{ px: padding, flex: '0 0 auto' }}>{children}</Box>
      </Box>

      {/* Footer (fixed at bottom) */}
      {finalFooter && (
        <Box
          component="footer"
          sx={{
            px: padding,
            py: 2,
            ...(autoFooter ? {} : { borderTop: 1, borderColor: 'divider' })
          }}
        >
          {finalFooter}
        </Box>
      )}
    </SwipeableDrawer>
  );
}
